import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { ApiError, apiRequest } from '../lib/api-client';

type CustomerStatus = 'ALL' | 'ACTIVE' | 'INACTIVE';
type PaymentMethod = 'CASH' | 'QRIS' | 'TRANSFER';

type Customer = {
  id: string;
  code: string;
  name: string;
  phone: string;
  address: string | null;
  notes: string | null;
  isActive: boolean;
  outlet: { id: string; code: string; name: string };
};

type CustomerDetail = Customer & {
  summary: {
    transactions: number;
    totalPurchases: number;
    grossProfit: number;
    productsSold: number;
    lastTransactionAt: string | null;
  };
  transactions: Array<{
    id: string;
    number: string;
    paymentMethod: PaymentMethod;
    total: number;
    createdAt: string;
    items: Array<{ quantity: number; product: { id: string; name: string; sku: string } }>;
  }>;
};

const PAGE_SIZE = 10;
const currency = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
});
const dateTime = new Intl.DateTimeFormat('id-ID', {
  dateStyle: 'medium',
  timeStyle: 'short',
});
const paymentLabel: Record<PaymentMethod, string> = {
  CASH: 'Tunai',
  QRIS: 'QRIS',
  TRANSFER: 'Transfer',
};

export function Customers({ canWrite, outletReady }: { canWrite: boolean; outletReady: boolean }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<CustomerStatus>('ALL');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState<Customer | null | undefined>(undefined);
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    address: '',
    notes: '',
    isActive: true,
  });

  async function load() {
    if (!outletReady) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search,
        status,
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      const result = await apiRequest<{
        data: Customer[];
        meta: { total: number };
      }>(`/customers?${params}`);
      setCustomers(result.data);
      setTotal(result.meta.total);
      setError('');
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Data customer gagal dimuat.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [outletReady, page, status]);

  function openForm(customer: Customer | null) {
    setEditing(customer);
    setForm({
      name: customer?.name ?? '',
      phone: customer?.phone ?? '',
      address: customer?.address ?? '',
      notes: customer?.notes ?? '',
      isActive: customer?.isActive ?? true,
    });
    setError('');
    setMessage('');
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('Nama pelanggan wajib diisi.');
      return;
    }
    if (form.phone.replaceAll(/\D/g, '').length < 8) {
      setError('Nomor WhatsApp minimal 8 digit.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await apiRequest(`/customers${editing ? `/${editing.id}` : ''}`, {
        method: editing ? 'PATCH' : 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          notes: form.notes.trim(),
          isActive: form.isActive,
        }),
      });
      setMessage(`Customer berhasil ${editing ? 'diperbarui' : 'ditambahkan'}.`);
      setEditing(undefined);
      if (page !== 1) setPage(1);
      else await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Customer gagal disimpan.');
    } finally {
      setBusy(false);
    }
  }

  async function showDetail(id: string) {
    setError('');
    try {
      setDetail(await apiRequest<CustomerDetail>(`/customers/${id}`));
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Detail customer gagal dimuat.');
    }
  }

  async function deactivate(customer: Customer) {
    if (
      !window.confirm(
        `Nonaktifkan ${customer.name}? Riwayat transaksi tetap tersimpan dan tidak akan dihapus.`,
      )
    )
      return;
    setError('');
    try {
      await apiRequest(`/customers/${customer.id}/deactivate`, { method: 'POST' });
      setMessage(`${customer.name} dinonaktifkan tanpa menghapus riwayat.`);
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Customer gagal dinonaktifkan.');
    }
  }

  if (!outletReady) {
    return (
      <div className="rounded-2xl bg-amber-50 p-6 text-amber-800">
        Belum ada outlet aktif. Pilih outlet sebelum membuka Customer.
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section className="page-shell">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="font-bold text-ajisai-700">Master Data Outlet</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight">Customer</h1>
          <p className="mt-2 text-sm text-slate-500">
            Data pelanggan outlet untuk pelayanan dan riwayat transaksi yang rapi.
          </p>
        </div>
        {canWrite && (
          <button
            className="rounded-xl bg-ajisai-700 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-ajisai-700/20"
            onClick={() => openForm(null)}
          >
            + Tambah Customer
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </p>
      )}
      {message && (
        <p className="mt-5 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-700">{message}</p>
      )}

      <div className="mt-7 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <form
          className="grid gap-3 border-b border-slate-200 p-4 sm:grid-cols-[minmax(0,1fr)_180px_auto]"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            void load();
          }}
        >
          <input
            aria-label="Cari customer"
            className="min-w-0 rounded-xl border border-slate-300 px-4 py-2.5 text-sm"
            placeholder="Cari nama, WhatsApp, atau ID customer"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <select
            aria-label="Filter status customer"
            className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as CustomerStatus);
              setPage(1);
            }}
          >
            <option value="ALL">Semua status</option>
            <option value="ACTIVE">Aktif</option>
            <option value="INACTIVE">Nonaktif</option>
          </select>
          <button className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold">
            Cari
          </button>
        </form>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">Customer</th>
                <th className="px-5 py-3">WhatsApp</th>
                <th className="px-5 py-3">Outlet Asal</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {customers.map((customer) => (
                <tr key={customer.id}>
                  <td className="px-5 py-4">
                    <p className="font-bold text-slate-900">{customer.name}</p>
                    <p className="mt-1 text-xs text-slate-500">{customer.code}</p>
                  </td>
                  <td className="px-5 py-4 font-medium">{customer.phone}</td>
                  <td className="px-5 py-4">
                    <p className="font-medium">{customer.outlet.name}</p>
                    <p className="mt-1 text-xs text-slate-500">{customer.outlet.code}</p>
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge active={customer.isActive} />
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 text-right">
                    <button
                      className="font-bold text-slate-700"
                      onClick={() => void showDetail(customer.id)}
                    >
                      Detail
                    </button>
                    {canWrite && (
                      <>
                        <button
                          className="ml-4 font-bold text-ajisai-700"
                          onClick={() => openForm(customer)}
                        >
                          Edit
                        </button>
                        {customer.isActive && (
                          <button
                            className="ml-4 font-bold text-red-600"
                            onClick={() => void deactivate(customer)}
                          >
                            Nonaktifkan
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="divide-y divide-slate-100 md:hidden">
          {customers.map((customer) => (
            <article key={customer.id} className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-black">{customer.name}</p>
                  <p className="mt-1 text-xs text-slate-500">{customer.code}</p>
                </div>
                <StatusBadge active={customer.isActive} />
              </div>
              <p className="mt-4 text-sm font-medium">WhatsApp: {customer.phone}</p>
              <p className="mt-1 text-sm text-slate-500">Outlet: {customer.outlet.name}</p>
              <div className="mt-4 flex flex-wrap gap-4 text-sm font-bold">
                <button className="text-slate-700" onClick={() => void showDetail(customer.id)}>
                  Detail
                </button>
                {canWrite && (
                  <button className="text-ajisai-700" onClick={() => openForm(customer)}>
                    Edit
                  </button>
                )}
                {canWrite && customer.isActive && (
                  <button className="text-red-600" onClick={() => void deactivate(customer)}>
                    Nonaktifkan
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>

        {loading && <p className="p-12 text-center text-sm text-slate-500">Memuat customer...</p>}
        {!loading && customers.length === 0 && (
          <p className="p-12 text-center text-sm text-slate-500">
            Belum ada customer pada outlet ini.
          </p>
        )}

        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-4 text-sm">
          <span className="text-slate-500">
            {total} customer · Halaman {page} dari {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              className="rounded-lg border px-3 py-2 disabled:opacity-40"
              disabled={page === 1}
              onClick={() => setPage((value) => value - 1)}
            >
              Sebelumnya
            </button>
            <button
              className="rounded-lg border px-3 py-2 disabled:opacity-40"
              disabled={page >= totalPages}
              onClick={() => setPage((value) => value + 1)}
            >
              Berikutnya
            </button>
          </div>
        </div>
      </div>

      {editing !== undefined && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-slate-950/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-bold text-ajisai-700">{editing ? 'Edit' : 'Tambah'}</p>
                <h2 className="text-2xl font-black">Customer</h2>
              </div>
              <button
                aria-label="Tutup"
                className="text-xl text-slate-500"
                onClick={() => setEditing(undefined)}
              >
                ×
              </button>
            </div>
            <form className="mt-6 grid gap-5 sm:grid-cols-2" onSubmit={(event) => void save(event)}>
              <label className="text-sm font-bold text-slate-700">
                Nama Pelanggan
                <input
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </label>
              <label className="text-sm font-bold text-slate-700">
                Nomor WhatsApp
                <input
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal"
                  required
                  maxLength={25}
                  inputMode="tel"
                  placeholder="Contoh: 08123456789"
                  value={form.phone}
                  onChange={(event) => setForm({ ...form, phone: event.target.value })}
                />
              </label>
              <label className="text-sm font-bold text-slate-700 sm:col-span-2">
                Alamat <span className="font-normal text-slate-400">(opsional)</span>
                <textarea
                  className="mt-2 min-h-20 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal"
                  maxLength={500}
                  value={form.address}
                  onChange={(event) => setForm({ ...form, address: event.target.value })}
                />
              </label>
              <label className="text-sm font-bold text-slate-700 sm:col-span-2">
                Catatan <span className="font-normal text-slate-400">(opsional)</span>
                <textarea
                  className="mt-2 min-h-24 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal"
                  maxLength={1000}
                  value={form.notes}
                  onChange={(event) => setForm({ ...form, notes: event.target.value })}
                />
              </label>
              <label className="sm:col-span-2 flex items-center gap-3 rounded-xl border border-slate-300 p-4 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                />
                Customer aktif
              </label>
              <div className="flex justify-end gap-3 sm:col-span-2">
                <button
                  type="button"
                  className="rounded-xl border px-4 py-3 font-bold"
                  onClick={() => setEditing(undefined)}
                >
                  Batal
                </button>
                <button
                  disabled={busy}
                  className="rounded-xl bg-ajisai-700 px-5 py-3 font-bold text-white disabled:opacity-50"
                >
                  {busy ? 'Menyimpan...' : 'Simpan Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {detail && <CustomerDetailDialog detail={detail} onClose={() => setDetail(null)} />}
    </section>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}
    >
      {active ? 'Aktif' : 'Nonaktif'}
    </span>
  );
}

function CustomerDetailDialog({
  detail,
  onClose,
}: {
  detail: CustomerDetail;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-bold text-ajisai-700">Detail Customer</p>
            <h2 className="mt-1 text-2xl font-black">{detail.name}</h2>
            <p className="mt-1 text-sm text-slate-500">
              {detail.code} · {detail.outlet.name}
            </p>
          </div>
          <button aria-label="Tutup detail" className="text-xl text-slate-500" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard label="Total Transaksi" value={String(detail.summary.transactions)} />
          <SummaryCard
            label="Total Pembelian"
            value={currency.format(detail.summary.totalPurchases)}
          />
          <SummaryCard label="Produk Terjual" value={String(detail.summary.productsSold)} />
          <SummaryCard
            label="Transaksi Terakhir"
            value={
              detail.summary.lastTransactionAt
                ? dateTime.format(new Date(detail.summary.lastTransactionAt))
                : 'Belum ada'
            }
          />
        </div>

        <div className="mt-6 grid gap-4 rounded-xl bg-slate-50 p-5 text-sm sm:grid-cols-2">
          <div>
            <p className="text-slate-500">WhatsApp</p>
            <p className="mt-1 font-bold">{detail.phone}</p>
          </div>
          <div>
            <p className="text-slate-500">Status</p>
            <div className="mt-1">
              <StatusBadge active={detail.isActive} />
            </div>
          </div>
          <div>
            <p className="text-slate-500">Alamat</p>
            <p className="mt-1 font-medium">{detail.address || '—'}</p>
          </div>
          <div>
            <p className="text-slate-500">Catatan</p>
            <p className="mt-1 font-medium">{detail.notes || '—'}</p>
          </div>
        </div>

        <div className="mt-7 overflow-hidden rounded-xl border border-slate-200">
          <div className="border-b border-slate-200 p-4">
            <h3 className="font-black">Riwayat Transaksi</h3>
            <p className="mt-1 text-xs text-slate-500">
              Maksimal 20 transaksi Penjualan terbaru yang terhubung.
            </p>
          </div>
          <div className="divide-y divide-slate-100">
            {detail.transactions.map((transaction) => (
              <article
                key={transaction.id}
                className="p-4 sm:flex sm:items-start sm:justify-between sm:gap-4"
              >
                <div>
                  <p className="font-bold">{transaction.number}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {dateTime.format(new Date(transaction.createdAt))} ·{' '}
                    {paymentLabel[transaction.paymentMethod]}
                  </p>
                  <p className="mt-2 text-sm text-slate-600">
                    {transaction.items
                      .map((item) => `${item.product.name} × ${item.quantity}`)
                      .join(', ')}
                  </p>
                </div>
                <p className="mt-3 font-black sm:mt-0">{currency.format(transaction.total)}</p>
              </article>
            ))}
            {detail.transactions.length === 0 && (
              <p className="p-10 text-center text-sm text-slate-500">
                Belum ada transaksi yang terhubung dengan customer ini.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-2 break-words font-black">{value}</p>
    </div>
  );
}
